import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { debounce } from 'throttle-debounce';
import RefundFormLabel from './RefundFormLabel';

export default function AutocompleteField({
  label,
  isRequired = false,
  placeholder,
  value,
  onChange,
  onSearch,
  options = [],
  onSelect,
  getOptionLabel,
  getOptionKey,
  isDisabled = false,
  isLoading = false,
  emptyMessage = 'Sin resultados',
  minLength = 1,
  debounceMs = 400,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);

  const debouncedSearch = useMemo(
    () =>
      debounce(debounceMs, (term) => {
        if (term.trim().length >= minLength) {
          onSearch?.(term.trim());
        }
      }),
    [debounceMs, minLength, onSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const next = e.target.value;
    onChange(next);
    setIsOpen(true);
    setHighlightedIndex(-1);
    debouncedSearch(next);
  };

  const handleSelect = (option) => {
    onSelect(option);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || !options.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1 >= options.length ? 0 : prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 < 0 ? options.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(options[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const canShowDropdown = value.trim().length >= minLength;
  const showDropdown = isOpen && canShowDropdown && !isDisabled;

  return (
    <FormControl ref={containerRef} position="relative">
      {label && <RefundFormLabel required={isRequired}>{label}</RefundFormLabel>}
      <InputGroup>
        <Input
          value={value}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => canShowDropdown && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          isDisabled={isDisabled}
          autoComplete="off"
        />
        {isLoading && (
          <InputRightElement height="100%">
            <Spinner size="sm" color="#574feb" />
          </InputRightElement>
        )}
      </InputGroup>

      {showDropdown && (
        <List
          position="absolute"
          zIndex={10}
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          maxH="220px"
          overflowY="auto"
        >
          {options.length === 0 && !isLoading && (
            <ListItem px={3} py={2} fontSize="sm" color="gray.500">
              {emptyMessage}
            </ListItem>
          )}
          {options.map((option, index) => (
            <ListItem
              key={getOptionKey(option)}
              px={3}
              py={2}
              fontSize="sm"
              cursor="pointer"
              bg={index === highlightedIndex ? '#574feb10' : 'white'}
              _hover={{ bg: '#574feb10' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(option)}
            >
              {getOptionLabel(option)}
            </ListItem>
          ))}
        </List>
      )}
    </FormControl>
  );
}
